import React from "react"
import {Group, Text} from "react-konva"
import {Line, DashedLine} from "../../../Renderer/primitives";
import {renderGroupElements as renderBlockOutGroupElements} from "../../../Element/blockOutRenderFactory";
import {renderMeasurements} from "../../../Element/rectangularElementFunctions";
import {
    renderGroupElements as renderCircularBlockOutGroupElements
} from "../../../Element/circularBlockOutRenderFactory";
import {FONT_SIZE} from "../../../../../constants";
import {getClosureSizes} from "../../../../domain/manhole";
import {getBevelHeight} from "../../../../domain/lid";
import {
    renderFillLabel,
    renderLidOutline,
    renderVaultOutlineBehindRects
} from "./renderer";
import {ConventionalManhole} from "./ConventionalManhole";
import {renderNoCollarLid} from "./NoCollarLid";
import {useSelector} from "react-redux";
import {makeSelectLidElementErrorIds} from "../../../../../features/plan/planSelectors";
import {createLidGeometry} from "./lidGeometry";
import {Plan, Manhole, RectangularBlockOut, CircularBlockOut} from "../../../../../types";

interface ConventionalLidProps {
    plan: Plan,
    manholes: Manhole[],
    scale: number,
}

const ConventionalLid: React.FC<ConventionalLidProps> = ({plan, manholes, scale}) => {
    const lidGeometry = createLidGeometry(plan, manholes, scale)
    const selectLidElementErrorIds = React.useMemo(makeSelectLidElementErrorIds, []);
    const lidElementErrorIds = useSelector((s) => selectLidElementErrorIds(s));
    const renderBlockOuts = () => {
        return plan.lid.blockOuts?.map((b: RectangularBlockOut, i: number) => {
            const getX = () => b.x * scale
            const getY = () => lidGeometry.clearWidth - (b.y + b.height) * scale
            const getWidth = () => b.width * scale
            const getHeight = () => b.height * scale
            const getScreenRect = () => ({x: getX(), y: getY(), width: getWidth(), height: getHeight()})

            return (<Group x={getX()} y={getY()} key={'block_out_' + i}>
                {renderBlockOutGroupElements(
                    getScreenRect(),
                    b,
                    'transparent',
                    lidElementErrorIds.includes(b.id!) ? 'red' : 'black'
                )}
                {renderMeasurements(getScreenRect(), b, true)}
            </Group>)
        })
    }

    const renderCircularBlockOuts = () => {
        return plan.lid.circularBlockOuts?.map((b: CircularBlockOut, i: number) => {
            const getRadius = () => b.outerDiameter / 2 * scale

            return (<Group
                key={'circular_manhole_' + i}
                x={b.x * scale}
                y={lidGeometry.clearWidth - b.y * scale}
            >
                {renderCircularBlockOutGroupElements(
                    {radius: b.diameter / 2 * scale},
                    b,
                    "transparent",
                    lidElementErrorIds.includes(b.id!) ? 'red' : 'black'
                )}
                <Text
                    x={-0.8 * getRadius() - 200}
                    y={0.8 * getRadius()}
                    width={200}
                    height={FONT_SIZE}
                    fontSize={FONT_SIZE}
                    align="right"
                    verticalAlign="top"
                    text={b.x + "/" + b.y}
                    fill="black"
                />
            </Group>)
        })
    }

    const renderBevelLines = (manhole: Manhole) => {
        const getRabbetWidth = () => getClosureSizes(manhole.type) ?
            getClosureSizes(manhole.type).rabbetWidth! * scale :
            0
        const getManholeBottom = () => lidGeometry.clearFront -
            manhole.y *
            scale +
            (manhole.orientation === 'vertical' ? getRabbetWidth() : 0)
        const getManholeTop = () => getManholeBottom() -
            manhole.height *
            scale -
            2 *
            (manhole.orientation === 'vertical' ? getRabbetWidth() : 0)
        const getManholeLeft = () => lidGeometry.clearLeft +
            manhole.x *
            scale -
            (manhole.orientation === 'horizontal' ? getRabbetWidth() : 0)
        const getManholeRight = () => getManholeLeft() +
            manhole.width *
            scale +
            2 *
            (manhole.orientation === 'horizontal' ? getRabbetWidth() : 0)

        return (<React.Fragment>
            <Line points={[lidGeometry.left, lidGeometry.top, getManholeLeft(), getManholeTop()]}/>
            <Line points={[lidGeometry.right, lidGeometry.top, getManholeRight(), getManholeTop()]}/>
            <Line points={[lidGeometry.right, lidGeometry.bottom, getManholeRight(), getManholeBottom()]}/>
            <Line points={[lidGeometry.left, lidGeometry.bottom, getManholeLeft(), getManholeBottom()]}/>
        </React.Fragment>)
    }

    const IntermediateCeilingsClearMeasure: React.FC<{ manhole: Manhole }> = ({manhole}) => {
        const getManholeLeft = () => lidGeometry.clearLeft + manhole.x * scale
        const getManholeWidth = () => manhole.width * scale
        const getManholeRight = () => getManholeLeft() + getManholeWidth()
        const getManholeBottom = () => lidGeometry.clearFront - manhole.y * scale
        const getManholeHeight = () => manhole.height * scale
        const getManholeTop = () => getManholeBottom() - getManholeHeight()
        const getXWithinClearMeasure = (x: number) => {
            return Math.min(lidGeometry.clearRight, Math.max(lidGeometry.clearLeft, x))
        }
        const getYWithinClearMeasure = (y: number) => {
            return Math.max(lidGeometry.clearBack, Math.min(lidGeometry.clearFront, y))
        }


        return manhole.orientation === 'vertical'
            ?
            <React.Fragment>
                {getManholeTop() < lidGeometry.clearBack &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeTop()),
                        getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeTop())]}/>}
                {getManholeBottom() > lidGeometry.clearFront &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeBottom()),
                        getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeBottom())]}/>}
                {getManholeLeft() < lidGeometry.clearLeft &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeTop()),
                        getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeBottom()),]}/>}
                {getManholeRight() > lidGeometry.clearRight &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeTop()),
                        getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeBottom()),]}/>}
            </React.Fragment>
            :
            <React.Fragment>
                {getManholeLeft() < lidGeometry.clearLeft &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeTop()),
                        getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeBottom()),]}/>}
                {getManholeRight() > lidGeometry.clearRight &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeTop()),
                        getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeBottom())]}/>}
                {getManholeTop() < lidGeometry.clearBack &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeTop()),
                        getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeTop())]}/>}
                {getManholeBottom() > lidGeometry.clearFront &&
                    <DashedLine points={[getXWithinClearMeasure(getManholeLeft()),
                        getYWithinClearMeasure(getManholeBottom()),
                        getXWithinClearMeasure(getManholeRight()),
                        getYWithinClearMeasure(getManholeBottom()),]}/>}
            </React.Fragment>
    }

    return (
        <React.Fragment>
            {plan.lid.collar !== 'without' && renderLidOutline(lidGeometry)}
            {plan.lid.collar === 'without' && renderNoCollarLid(plan, manholes[0], scale, lidGeometry)}
            {
                manholes.map((m, i) =>
                    <ConventionalManhole
                        key={'conventional_manhole_' + i}
                        type={plan.lid.type}
                        fill={plan.lid.fill}
                        manhole={m}
                        scale={scale}
                        lidGeometry={lidGeometry}
                        outlineColor={lidElementErrorIds.includes(m.id) ? 'red' : 'black'}
                    />
                )
            }
            {manholes.length > 0 && getBevelHeight(plan) > 0 && renderBevelLines(manholes[0])}
            {!plan.intermediateCeiling && plan.lid.collar === 'lifted' &&
                <Group x={(lidGeometry.clearLeft - lidGeometry.left) / 2} y={lidGeometry.height / 2}>
                    {renderFillLabel(plan.lid.fill)}
                </Group>}
            {!!plan.intermediateCeiling && plan.lid.collar === 'lifted' &&
                <Group x={lidGeometry.left + 5 * scale} y={lidGeometry.top + lidGeometry.height / 2}>
                    {renderFillLabel(plan.lid.fill)}
                </Group>}
            <Group x={lidGeometry.clearLeft} y={lidGeometry.clearBack}>
                {renderBlockOuts()}
                {renderCircularBlockOuts()}
            </Group>
            {
                !!plan.intermediateCeiling &&
                manholes.map((m, i) => <IntermediateCeilingsClearMeasure manhole={m}
                                                                         key={'is_clear_measure_' + i}/>)
            }
        </React.Fragment>
    )
}

export const ConventionalLids: React.FC<{ plan: Plan, scale: number }> = ({plan, scale}) => {
    if (!!plan.intermediateCeiling && plan.lid.manholes.length > 0) {
        const lidGeometry = createLidGeometry(plan, plan.lid.manholes, scale)
        const vaultOutline = {
            x: lidGeometry.vaultLeft,
            y: lidGeometry.vaultBack,
            w: lidGeometry.vaultRight - lidGeometry.vaultLeft,
            h: lidGeometry.vaultFront - lidGeometry.vaultBack
        }
        const rects = plan.lid.manholes.map((m, _) => {
            const lg = createLidGeometry(plan, [m], scale)

            return {x: lg.left, y: lg.top, w: lg.width, h: lg.height}
        })

        return (
            <React.Fragment>
                {
                    plan.lid.manholes.map((m, i) =>
                        <ConventionalLid plan={plan} manholes={[m]} scale={scale} key={'conventional_lid_' + i}/>
                    )
                }
                {
                    renderVaultOutlineBehindRects(vaultOutline, rects)
                }
            </React.Fragment>
        )
    }

    return <ConventionalLid plan={plan} manholes={plan.lid.manholes} scale={scale}/>
}
